/**
 * Environment variable parsing utilities
 * @module utils/env
 */

/**
 * @typedef {string|undefined|null} EnvValue
 * A value that may come from process.env (string, undefined, or null)
 */

/**
 * Parse an environment variable as an integer with fallback.
 * Returns defaultValue if the value is undefined, empty, or not a valid integer.
 *
 * @example
 * // Returns 5000 if REACT_APP_TIMEOUT is "5000"
 * parseEnvInt(process.env.REACT_APP_TIMEOUT, 3000)
 *
 * @example
 * // Returns 3000 (default) if REACT_APP_TIMEOUT is undefined or invalid
 * parseEnvInt(process.env.REACT_APP_TIMEOUT, 3000)
 *
 * @param {EnvValue} value - The environment variable value
 * @param {number} defaultValue - Fallback value if parsing fails
 * @returns {number} Parsed integer or default value
 */
export function parseEnvInt(value, defaultValue) {
  if (value === undefined || value === null || value === "") {
    return defaultValue;
  }
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? defaultValue : parsed;
}

/**
 * Parse an environment variable as a boolean.
 * Returns true for "true", "1", "yes" (case-insensitive), false otherwise.
 *
 * @example
 * // Returns true if REACT_APP_DEBUG is "true", "1", or "yes"
 * parseEnvBool(process.env.REACT_APP_DEBUG, false)
 *
 * @example
 * // Returns false (default) if REACT_APP_DEBUG is undefined or any other value
 * parseEnvBool(process.env.REACT_APP_DEBUG)
 *
 * @param {EnvValue} value - The environment variable value
 * @param {boolean} [defaultValue=false] - Fallback value if undefined
 * @returns {boolean} Parsed boolean or default value
 */
export function parseEnvBool(value, defaultValue = false) {
  if (value === undefined || value === null || value === "") {
    return defaultValue;
  }
  return ["true", "1", "yes"].includes(value.toLowerCase());
}

/**
 * Parse an environment variable as a string with fallback.
 * Returns defaultValue if the value is undefined, null, or empty.
 *
 * @example
 * // Returns "production" if NODE_ENV is "production"
 * parseEnvString(process.env.NODE_ENV, "development")
 *
 * @param {EnvValue} value - The environment variable value
 * @param {string} defaultValue - Fallback value if empty/undefined
 * @returns {string} Trimmed string value or default value
 */
export function parseEnvString(value, defaultValue) {
  if (value === undefined || value === null) {
    return defaultValue;
  }
  const trimmed = value.trim();
  return trimmed === "" ? defaultValue : trimmed;
}
